import { ensureUserTestId } from "@vp/ab-test-cookie";
import { PricingClient } from "../clients/pricingClient";
import { priceEventTarget } from "../components/PriceDisplay/getProductPriceData";
import type { ProductPricingInput } from "../types/pricing/price-display.types";
import { PRICE_UPDATE_EVENT } from "../types/pricing/price-display.types";
import type { LowestPriceQuery, LowestPriceRequest } from "../types/pricing/price-request.types";
import type { ProductPriceDetails } from "../types/pricing/product-price-details.types";
import { getPriceQueryId } from "../util/price-queryId";

interface PricingContext {
  value: string | undefined;
  isInitialized: boolean;
}

type State = {
  debounceMs: number;
  debounceTimer: ReturnType<typeof setTimeout> | null;
  productPricingDetails: Record<string, ProductPriceDetails>;
  priceQueryMap: Record<string, LowestPriceQuery>;
  priceQueriesLoading: Record<string, LowestPriceQuery>;
  isError: boolean;
  pricingContext: PricingContext;
  vatIncl: boolean;
  tenant: string;
  locale: string;
};

class ProductPricingService {
  private static instance: ProductPricingService;
  private client: PricingClient;
  private state: State = {
    debounceMs: 100,
    debounceTimer: null,
    productPricingDetails: {},
    priceQueryMap: {},
    priceQueriesLoading: {},
    isError: false,
    pricingContext: { value: undefined, isInitialized: false },
    vatIncl: false,
    tenant: "vistaprint",
    locale: "",
  };

  private constructor() {
    this.client = new PricingClient();
  }

  static getInstance(): ProductPricingService {
    if (!this.instance) {
      this.instance = new ProductPricingService();
    }
    return this.instance;
  }

  /* ---------------------------- GETTERS ---------------------------- */
  get pricingDetails(): Record<string, ProductPriceDetails> {
    return this.state.productPricingDetails;
  }

  get pricingContext(): PricingContext {
    return this.state.pricingContext;
  }

  get vatIncl() {
    return this.state.vatIncl ?? false;
  }

  get locale(): string {
    return this.state.locale;
  }

  get tenant(): string {
    return this.state.tenant;
  }

  get isError(): boolean {
    return this.state.isError;
  }

  /* ---------------------------- SETTERS ---------------------------- */
  set vatIncl(vatIncl: boolean) {
    this.state.vatIncl = vatIncl;
  }

  set locale(locale: string) {
    if (locale !== this.state.locale) {
      this.state.locale = locale;
      this.clearCache();
    }
  }

  set tenant(tenant: string) {
    if (tenant !== this.state.tenant) {
      this.state.tenant = tenant;
      this.clearCache();
    }
  }

  set pricingContext(context: string | undefined) {
    if (context !== this.state.pricingContext.value) {
      this.state.pricingContext = {
        value: context ?? "",
        isInitialized: context !== undefined,
      };
      this.clearCache();
    }
  }

  /* ---------------------------- CACHE ---------------------------- */
  clearCache(): void {
    this.state.productPricingDetails = {};
    this.state.priceQueryMap = {};
    this.state.priceQueriesLoading = {};
  }

  invalidateQuery(id: string): void {
    delete this.state.productPricingDetails[id];
    delete this.state.priceQueryMap[id];
    delete this.state.priceQueriesLoading[id];
  }

  /* ---------------------------- QUEUE & FETCH ---------------------------- */
  queuePriceQuery(query: ProductPricingInput): void {
    const { mpvId, productId, version } = query;
    if (!mpvId && !(productId && version)) return;
    const id = getPriceQueryId(query);
    if (!id) return;

    // Skip if already fetched or loading
    if (this.state.productPricingDetails[id] || this.state.priceQueriesLoading[id]) return;
    const lowestPriceQuery: LowestPriceQuery = {
      ...query,
      id,
    };

    this.state.priceQueryMap[id] = lowestPriceQuery;
    this.debounceFetch();
  }

  private debounceFetch(): void {
    if (this.state.debounceTimer) clearTimeout(this.state.debounceTimer);
    this.state.debounceTimer = setTimeout(() => this.fetchPrices(), this.state.debounceMs);
  }

  private async fetchPrices(): Promise<void> {
    const request = this.buildRequest();
    if (!request) return;

    this.state.priceQueriesLoading = { ...this.state.priceQueriesLoading, ...this.state.priceQueryMap };
    this.state.priceQueryMap = {};

    try {
      const productPricingDetails = await this.client.fetchProductPriceDetails(request);

      this.state.productPricingDetails = { ...this.state.productPricingDetails, ...productPricingDetails };
      this.state.priceQueriesLoading = {};
      this.state.isError = false;

      // Dispatch a custom event to notify our component about prices are ready and to SEO-related functions price data is available
      priceEventTarget.dispatchEvent(new CustomEvent(PRICE_UPDATE_EVENT, { detail: productPricingDetails }));
    } catch (err) {
      console.error("Error fetching product price details", { request, err });
      this.state.isError = true;
      this.state.priceQueriesLoading = {};
    }
  }

  private buildRequest(): LowestPriceRequest | null {
    const queries: LowestPriceQuery[] = Object.values(this.state.priceQueryMap);
    const { tenant, locale, pricingContext } = this.state;
    const testUserId = ensureUserTestId();

    if (!pricingContext.isInitialized || queries.length === 0) return null;

    return {
      tenant,
      locale,
      requestor: "price-platform",
      pricingContext: pricingContext.value,
      priceQueries: queries,
      testUserId,
    };
  }
}

export const productPricingService = ProductPricingService.getInstance();
